import { supabase } from '../../lib/supabase';
import { 
  Drill, 
  DrillStep
} from '../../types';

// Save generated drills to the database (REPLACES ALL EXISTING DRILLS)
// Use this for AI-generated practice plans that should replace everything
export const saveDrills = async (practiceId: string, drills: Drill[]): Promise<void> => {
  try {
    if (!practiceId || !drills || drills.length === 0) {
      throw new Error('Missing practice ID or drills');
    }

    console.log(`[saveDrills] Starting to save ${drills.length} drills for practice ${practiceId}`);

    // First, clear existing drills for this practice to start fresh
    console.log('[saveDrills] Clearing existing drills...');
    const { error: clearError } = await supabase
      .from('drills')
      .delete()
      .eq('practice_id', practiceId);
    
    if (clearError) {
      console.error('[saveDrills] Error clearing existing drills:', clearError);
      throw clearError;
    }

    // Process each drill
    for (let i = 0; i < drills.length; i++) {
      const drill = drills[i];
      await saveSingleDrillInternal(practiceId, drill, i);
    }
    
    console.log(`[saveDrills] Successfully saved all ${drills.length} drills`);
  } catch (error) {
    console.error('[saveDrills] Error saving drills:', error);
    throw new Error('Failed to save drills');
  }
};

// Add a single drill without affecting existing drills (PRESERVES EXISTING DRILLS)
// Use this for manually adding individual drills
export const addSingleDrill = async (practiceId: string, drill: Omit<Drill, 'id'>): Promise<Drill> => {
  try {
    if (!practiceId || !drill) {
      throw new Error('Missing practice ID or drill data');
    }

    console.log(`[addSingleDrill] Adding drill "${drill.name}" to practice ${practiceId}`);

    // Get the current highest order_index for this practice
    const { data: maxOrderData } = await supabase
      .from('drills')
      .select('order_index')
      .eq('practice_id', practiceId)
      .order('order_index', { ascending: false })
      .limit(1);
    
    const nextOrderIndex = (maxOrderData?.[0]?.order_index ?? -1) + 1;
    console.log(`[addSingleDrill] Using order index: ${nextOrderIndex}`);

    // Save the drill and get its ID
    const savedDrill = await saveSingleDrillInternal(practiceId, drill, nextOrderIndex);
    
    console.log(`[addSingleDrill] Successfully added drill "${drill.name}" with ID: ${savedDrill.id}`);
    return savedDrill;
  } catch (error) {
    console.error('[addSingleDrill] Error adding drill:', error);
    throw new Error(`Failed to add drill: ${drill.name}`);
  }
};

// Update a single drill without affecting existing drills (PRESERVES EXISTING DRILLS)
// Use this for manually editing individual drills
export const updateSingleDrill = async (drill: Drill): Promise<Drill> => {
  console.log(`[updateSingleDrill] Updating drill "${drill.name}" (ID: ${drill.id})`);
  
  try {
    // Add timeout to prevent hanging requests
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Drill update timeout')), 20000); // 20 second timeout
    });

    const updatePromise = (async () => {
      // Update the main drill record
      const { error: updateError } = await supabase
        .from('drills')
        .update({
          name: drill.name,
          duration: drill.duration,
          description: drill.description,
          focus_area: drill.focus_areas?.[0] || 'Team',
          intensity_level: (drill as any).intensity_level ?? null // NULL for water breaks, or the provided value
        })
        .eq('id', drill.id);

      if (updateError) {
        console.error(`[updateSingleDrill] Error updating drill:`, updateError);
        throw updateError;
      }

      // Update related data (position groups, steps, coaching points)
      await updateDrillRelatedData(drill.id, drill);

      console.log(`[updateSingleDrill] Updated drill ${drill.name}`);
      return drill;
    })();

    // Race between the update and timeout
    return await Promise.race([updatePromise, timeoutPromise]);
  } catch (error) {
    console.error(`[updateSingleDrill] Error updating drill:`, error);
    throw new Error(`Failed to update drill: ${drill.name}`);
  }
};

// Helper function to efficiently update drill's position groups, steps, and coaching points
const updateDrillRelatedData = async (drillId: string, drill: Drill) => {
  const positionGroups = drill.position_groups || [];
  
  // Get existing position groups for this drill
  const { data: existingPositionGroups, error: pgError } = await supabase
    .from('position_groups')
    .select('id, group_name')
    .eq('drill_id', drillId);
  
  if (pgError) {
    console.error(`[updateDrillRelatedData] Error fetching existing position groups:`, pgError);
    throw pgError;
  }

  // Process each position group from the updated drill
  for (const pg of positionGroups) {
    if (typeof pg === 'string') {
      // Handle string position groups - check if it already exists
      const existingPg = existingPositionGroups?.find(epg => epg.group_name === pg);
      if (!existingPg) {
        // Only create if it doesn't exist
        const { error: insertError } = await supabase
          .from('position_groups')
          .insert({
            drill_id: drillId,
            group_name: pg
          });
        
        if (insertError) {
          console.error(`[updateDrillRelatedData] Error inserting position group ${pg}:`, insertError);
          throw insertError;
        }
      }
    } else {
      // Handle object position groups with steps
      let positionGroupId: string;
      const existingPg = existingPositionGroups?.find(epg => epg.group_name === pg.group_name);
      
      if (existingPg) {
        // Use existing position group
        positionGroupId = existingPg.id;
      } else {
        // Create new position group
        const { data: newPg, error: insertError } = await supabase
          .from('position_groups')
          .insert({
            drill_id: drillId,
            group_name: pg.group_name
          })
          .select('id')
          .single();
        
        if (insertError) {
          console.error(`[updateDrillRelatedData] Error inserting position group ${pg.group_name}:`, insertError);
          throw insertError;
        }
        
        positionGroupId = newPg.id;
      }

      // Update steps for this position group
      await updatePositionGroupSteps(positionGroupId, pg.steps || [], drillId);
    }
  }

  // Remove position groups that are no longer in the drill
  const updatedGroupNames = positionGroups
    .map(pg => typeof pg === 'string' ? pg : pg.group_name);
  
  const groupsToRemove = existingPositionGroups?.filter(epg => 
    !updatedGroupNames.includes(epg.group_name)
  ) || [];

  for (const groupToRemove of groupsToRemove) {
    // Delete steps and coaching points for this group first
    await deletePositionGroupSteps(groupToRemove.id);
    
    // Then delete the position group
    const { error: deleteError } = await supabase
      .from('position_groups')
      .delete()
      .eq('id', groupToRemove.id);
    
    if (deleteError) {
      console.error(`[updateDrillRelatedData] Error deleting position group ${groupToRemove.group_name}:`, deleteError);
      throw deleteError;
    }
  }
};

// Helper function to update steps for a position group
const updatePositionGroupSteps = async (positionGroupId: string, newSteps: DrillStep[], drillId: string) => {
  // Get existing steps for this position group
  const { data: existingSteps, error: stepsError } = await supabase
    .from('drill_steps')
    .select('id, name, order_index')
    .eq('position_group_id', positionGroupId)
    .order('order_index');
  
  if (stepsError) {
    console.error(`[updatePositionGroupSteps] Error fetching existing steps:`, stepsError);
    throw stepsError;
  }

  // Prepare batch operations
  const stepsToUpdate: any[] = [];
  const stepsToInsert: any[] = [];
  const stepsToDelete: string[] = [];

  // Process existing steps
  for (let i = 0; i < Math.max(existingSteps?.length || 0, newSteps.length); i++) {
    const newStep = newSteps[i];
    const existingStep = existingSteps?.[i];
    
    if (existingStep && newStep) {
      // Update existing step
      stepsToUpdate.push({
        id: existingStep.id,
        name: newStep.name,
        setup: newStep.setup || '',
        execution: newStep.execution || '',
        equipment: newStep.equipment || '',
        video_url: newStep.video_url || '',
        order_index: i
      });
    } else if (existingStep && !newStep) {
      // Delete step that no longer exists
      stepsToDelete.push(existingStep.id);
    } else if (!existingStep && newStep) {
      // Insert new step
      stepsToInsert.push({
        drill_id: drillId,
        position_group_id: positionGroupId,
        name: newStep.name,
        setup: newStep.setup || '',
        execution: newStep.execution || '',
        equipment: newStep.equipment || '',
        video_url: newStep.video_url || '',
        order_index: i
      });
    }
  }

  // Execute batch operations
  try {
    // Delete steps that are no longer needed
    if (stepsToDelete.length > 0) {
      // Delete coaching points for steps to be deleted
      const { error: deleteCoachingPointsError } = await supabase
        .from('drill_coaching_points')
        .delete()
        .in('drill_step_id', stepsToDelete);
      
      if (deleteCoachingPointsError) {
        console.error(`[updatePositionGroupSteps] Error deleting coaching points for deleted steps:`, deleteCoachingPointsError);
        throw deleteCoachingPointsError;
      }

      // Delete the steps
      const { error: deleteStepsError } = await supabase
        .from('drill_steps')
        .delete()
        .in('id', stepsToDelete);
      
      if (deleteStepsError) {
        console.error(`[updatePositionGroupSteps] Error deleting steps:`, deleteStepsError);
        throw deleteStepsError;
      }
    }

    // Insert new steps
    if (stepsToInsert.length > 0) {
      const { data: insertedSteps, error: insertError } = await supabase
        .from('drill_steps')
        .insert(stepsToInsert)
        .select('id, order_index');
      
      if (insertError) {
        console.error(`[updatePositionGroupSteps] Error inserting new steps:`, insertError);
        throw insertError;
      }

      // Insert coaching points for new steps
      for (let i = 0; i < stepsToInsert.length; i++) {
        const newStep = newSteps[stepsToInsert[i].order_index];
        if (newStep.coaching_points && newStep.coaching_points.length > 0) {
          await insertStepCoachingPoints(insertedSteps![i].id, newStep.coaching_points);
        }
      }
    }

    // Update existing steps individually to avoid RLS issues
    if (stepsToUpdate.length > 0) {
      for (const stepUpdate of stepsToUpdate) {
        const { error: updateError } = await supabase
          .from('drill_steps')
          .update({
            name: stepUpdate.name,
            setup: stepUpdate.setup,
            execution: stepUpdate.execution,
            equipment: stepUpdate.equipment,
            video_url: stepUpdate.video_url,
            order_index: stepUpdate.order_index
          })
          .eq('id', stepUpdate.id);
        
        if (updateError) {
          console.error(`[updatePositionGroupSteps] Error updating step ${stepUpdate.id}:`, updateError);
          throw updateError;
        }

        // Update coaching points for this step
        const newStep = newSteps[stepUpdate.order_index];
        if (newStep.coaching_points) {
          await updateStepCoachingPoints(stepUpdate.id, newStep.coaching_points);
        }
      }
    }
  } catch (error) {
    console.error(`[updatePositionGroupSteps] Error in batch operations:`, error);
    throw error;
  }
};

// Helper function to update coaching points for a step
const updateStepCoachingPoints = async (stepId: string, newCoachingPoints: string[]) => {
  // Get existing coaching points
  const { data: existingPoints, error: pointsError } = await supabase
    .from('drill_coaching_points')
    .select('id, point, order_index')
    .eq('drill_step_id', stepId)
    .order('order_index');
  
  if (pointsError) {
    console.error(`[updateStepCoachingPoints] Error fetching existing coaching points:`, pointsError);
    throw pointsError;
  }

  // Prepare batch operations
  const pointsToUpdate: any[] = [];
  const pointsToInsert: any[] = [];
  const pointsToDelete: string[] = [];

  // Process existing points
  for (let i = 0; i < Math.max(existingPoints?.length || 0, newCoachingPoints.length); i++) {
    const newPoint = newCoachingPoints[i];
    const existingPoint = existingPoints?.[i];
    
    if (existingPoint && newPoint) {
      // Update existing point
      pointsToUpdate.push({
        id: existingPoint.id,
        point: newPoint,
        order_index: i
      });
    } else if (existingPoint && !newPoint) {
      // Delete point that no longer exists
      pointsToDelete.push(existingPoint.id);
    } else if (!existingPoint && newPoint) {
      // Insert new point
      pointsToInsert.push({
        drill_step_id: stepId,
        point: newPoint,
        order_index: i
      });
    }
  }

  // Execute batch operations
  try {
    // Delete points that are no longer needed
    if (pointsToDelete.length > 0) {
      const { error: deleteError } = await supabase
        .from('drill_coaching_points')
        .delete()
        .in('id', pointsToDelete);
      
      if (deleteError) {
        console.error(`[updateStepCoachingPoints] Error deleting coaching points:`, deleteError);
        throw deleteError;
      }
    }

    // Insert new points
    if (pointsToInsert.length > 0) {
      const { error: insertError } = await supabase
        .from('drill_coaching_points')
        .insert(pointsToInsert);
      
      if (insertError) {
        console.error(`[updateStepCoachingPoints] Error inserting new coaching points:`, insertError);
        throw insertError;
      }
    }

    // Update existing points individually to avoid RLS issues
    if (pointsToUpdate.length > 0) {
      for (const pointUpdate of pointsToUpdate) {
        const { error: updateError } = await supabase
          .from('drill_coaching_points')
          .update({
            point: pointUpdate.point,
            order_index: pointUpdate.order_index
          })
          .eq('id', pointUpdate.id);
        
        if (updateError) {
          console.error(`[updateStepCoachingPoints] Error updating coaching point ${pointUpdate.id}:`, updateError);
          throw updateError;
        }
      }
    }
  } catch (error) {
    console.error(`[updateStepCoachingPoints] Error in batch operations:`, error);
    throw error;
  }
};

// Helper function to insert coaching points for a new step
const insertStepCoachingPoints = async (stepId: string, coachingPoints: string[]) => {
  if (coachingPoints.length === 0) return;
  
  const pointsToInsert = coachingPoints.map((point, index) => ({
    drill_step_id: stepId,
    point,
    order_index: index
  }));
  
  const { error: insertError } = await supabase
    .from('drill_coaching_points')
    .insert(pointsToInsert);
  
  if (insertError) {
    console.error(`[insertStepCoachingPoints] Error inserting coaching points:`, insertError);
    throw insertError;
  }
};

// Helper function to delete all coaching points for a step
const deleteStepCoachingPoints = async (stepId: string) => {
  const { error: deleteError } = await supabase
    .from('drill_coaching_points')
    .delete()
    .eq('drill_step_id', stepId);
  
  if (deleteError) {
    console.error(`[deleteStepCoachingPoints] Error deleting coaching points for step ${stepId}:`, deleteError);
    throw deleteError;
  }
};

// Helper function to delete all steps and coaching points for a position group
const deletePositionGroupSteps = async (positionGroupId: string) => {
  // Get all step IDs for this position group
  const { data: steps, error: stepsError } = await supabase
    .from('drill_steps')
    .select('id')
    .eq('position_group_id', positionGroupId);
  
  if (stepsError) {
    console.error(`[deletePositionGroupSteps] Error fetching steps for position group ${positionGroupId}:`, stepsError);
    throw stepsError;
  }
  
  // Delete coaching points for all steps in batch
  if (steps && steps.length > 0) {
    const stepIds = steps.map(step => step.id);
    const { error: deleteCoachingPointsError } = await supabase
      .from('drill_coaching_points')
      .delete()
      .in('drill_step_id', stepIds);
    
    if (deleteCoachingPointsError) {
      console.error(`[deletePositionGroupSteps] Error deleting coaching points for position group ${positionGroupId}:`, deleteCoachingPointsError);
      throw deleteCoachingPointsError;
    }
  }
  
  // Delete all steps in batch
  const { error: deleteError } = await supabase
    .from('drill_steps')
    .delete()
    .eq('position_group_id', positionGroupId);
  
  if (deleteError) {
    console.error(`[deletePositionGroupSteps] Error deleting steps for position group ${positionGroupId}:`, deleteError);
    throw deleteError;
  }
};

// Internal helper function to save a single drill
const saveSingleDrillInternal = async (practiceId: string, drill: Omit<Drill, 'id'>, orderIndex: number): Promise<Drill> => {
  console.log(`[saveSingleDrillInternal] Processing drill: ${drill.name}`);
  
  try {
    // Determine intensity_level: use provided value, or default to "Technique focused" (unless water break)
    let intensityLevel: string | null = (drill as any).intensity_level;
    if (intensityLevel === undefined) {
      // Check if it's a water break
      const drillNameLower = (drill.name || '').toLowerCase();
      const isWaterBreak = drillNameLower.includes('water') || 
                          drillNameLower.includes('hydration') || 
                          (drillNameLower.includes('break') && (drillNameLower.includes('rest') || drillNameLower.includes('hydrate')));
      intensityLevel = isWaterBreak ? null : 'Technique focused';
      console.log(`[saveSingleDrillInternal] Setting default intensity_level for "${drill.name}": ${intensityLevel}`);
    } else {
      console.log(`[saveSingleDrillInternal] Using provided intensity_level for "${drill.name}": ${intensityLevel}`);
    }
    
    // Insert the drill
    const { data: drillData, error: drillError } = await supabase
      .from('drills')
      .insert({
        practice_id: practiceId,
        name: drill.name,
        duration: drill.duration,
        description: drill.description || '',
        focus_area: drill.focus_areas?.[0] || 'Team',
        order_index: orderIndex,
        intensity_level: intensityLevel
      })
      .select('id')
      .single();
    
    if (drillError) {
      console.error(`[saveSingleDrillInternal] Error inserting drill ${drill.name}:`, drillError);
      throw drillError;
    }
    
    const drillId = drillData.id;
    console.log(`[saveSingleDrillInternal] Inserted drill ${drill.name} with ID: ${drillId}`);
    
    // Process position groups for this drill
    const positionGroups = drill.position_groups || [];
    if (positionGroups.length === 0) {
      console.log(`[saveSingleDrillInternal] No position groups for drill ${drill.name}, skipping...`);
      return { ...drill, id: drillId };
    }

    for (const pg of positionGroups) {
      if (typeof pg === 'string') {
        // Handle string position groups (create empty position group)
        const { error: pgError } = await supabase
          .from('position_groups')
          .insert({
            drill_id: drillId,
            group_name: pg
          });
        
        if (pgError) {
          console.error(`[saveSingleDrillInternal] Error inserting position group ${pg}:`, pgError);
          throw pgError;
        }
      } else {
        // Insert position group
        const { data: pgData, error: pgError } = await supabase
          .from('position_groups')
          .insert({
            drill_id: drillId,
            group_name: pg.group_name
          })
          .select('id')
          .single();
        
        if (pgError) {
          console.error(`[saveSingleDrillInternal] Error inserting position group ${pg.group_name}:`, pgError);
          throw pgError;
        }
        
        const positionGroupId = pgData.id;
        console.log(`[saveSingleDrillInternal] Inserted position group ${pg.group_name} with ID: ${positionGroupId}`);
        
        // Process steps for this position group
        const steps = pg.steps || [];
        if (steps.length === 0) {
          console.log(`[saveSingleDrillInternal] No steps for position group ${pg.group_name}, skipping...`);
          continue;
        }

        // Prepare steps for batch insert
        const stepInserts = steps.map((step, stepIndex) => ({
          drill_id: drillId,
          position_group_id: positionGroupId,
          name: step.name,
          setup: step.setup || '',
          execution: step.execution || '',
          equipment: step.equipment || '',
          video_url: step.video_url || '',
          order_index: stepIndex
        }));

        // Batch insert steps
        const { data: insertedSteps, error: stepsError } = await supabase
          .from('drill_steps')
          .insert(stepInserts)
          .select('id');
        
        if (stepsError) {
          console.error(`[saveSingleDrillInternal] Error inserting steps for position group ${pg.group_name}:`, stepsError);
          throw stepsError;
        }

        console.log(`[saveSingleDrillInternal] Inserted ${insertedSteps?.length || 0} steps for position group ${pg.group_name}`);
        
        // Process coaching points for all steps
        const allCoachingPoints: any[] = [];
        
        steps.forEach((step, stepIndex) => {
          if (step.coaching_points && step.coaching_points.length > 0) {
            const stepId = insertedSteps?.[stepIndex]?.id;
            if (stepId) {
              step.coaching_points.forEach((point, pointIndex) => {
                allCoachingPoints.push({
                  drill_step_id: stepId,
                  point,
                  order_index: pointIndex
                });
              });
            }
          }
        });

        // Batch insert coaching points if any exist
        if (allCoachingPoints.length > 0) {
          const { error: coachingPointsError } = await supabase
            .from('drill_coaching_points')
            .insert(allCoachingPoints);
          
          if (coachingPointsError) {
            console.error(`[saveSingleDrillInternal] Error inserting coaching points for position group ${pg.group_name}:`, coachingPointsError);
            throw coachingPointsError;
          }

          console.log(`[saveSingleDrillInternal] Inserted ${allCoachingPoints.length} coaching points for position group ${pg.group_name}`);
        }
      }
    }
    
    console.log(`[saveSingleDrillInternal] Successfully saved drill ${drill.name}`);
    return { ...drill, id: drillId };
  } catch (drillError) {
    console.error(`[saveSingleDrillInternal] Failed to save drill ${drill.name}:`, drillError);
    throw drillError;
  }
}; 